import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Session } from '../api/types';
import Sheet from './ui/Sheet';
import Rosette from './ui/Rosette';
import PlayerNameInput from './PlayerNameInput';
import { TrophyIcon } from './ui/icons';
import { useProvisionalRank } from '../hooks/useProvisionalRank';
import { parseServerDate, formatClock, formatShortDate } from '../utils/dateUtils';
import { ordinal } from '../utils/ordinal';
import { slugify } from '../utils/urlUtils';
import './CompletionSheet.css';

interface CompletionSheetProps {
  isOpen: boolean;
  session: Session | null;
  onClose: () => void;
  onNameChange: (name: string) => Promise<void>;
}

/**
 * Stays mounted (isOpen toggles Sheet's own exit animation) even though its
 * `session` prop goes null once the caller clears it — the last known
 * session is retained locally so the celebration doesn't blank out mid-close.
 */
const CompletionSheet: React.FC<CompletionSheetProps> = ({
  isOpen,
  session,
  onClose,
  onNameChange,
}) => {
  const navigate = useNavigate();
  const [lastSession, setLastSession] = useState<Session | null>(session);

  useEffect(() => {
    if (session) setLastSession(session);
  }, [session]);

  const displaySession = session ?? lastSession;
  const rank = useProvisionalRank(isOpen ? displaySession : null);

  const handlePost = () => {
    onClose();
    if (displaySession?.crawlName) {
      navigate(`/crawls/${slugify(displaySession.crawlName)}`);
    } else {
      navigate('/crawls');
    }
  };

  if (!displaySession) {
    return <Sheet isOpen={isOpen} onClose={onClose} testId="completion-sheet" />;
  }

  const startTime = parseServerDate(displaySession.sessionStartTime);
  const endTime = displaySession.sessionEndTime
    ? parseServerDate(displaySession.sessionEndTime)
    : new Date();
  const durationSeconds = Math.max(
    0,
    Math.floor((endTime.getTime() - startTime.getTime()) / 1000)
  );

  return (
    <Sheet isOpen={isOpen} onClose={onClose} testId="completion-sheet">
      <div className="completion-sheet-celebrate">
        <Rosette count={displaySession.pubs.length} />
        <h2>All {displaySession.pubs.length}. Done.</h2>
        <div className="completion-sheet-final-label">Final time</div>
        <div className="completion-sheet-final tabular">{formatClock(durationSeconds)}</div>
        {rank !== null && (
          <span className="completion-sheet-rank">
            <TrophyIcon />
            {ordinal(rank)} on {displaySession.crawlName || 'this crawl'}
          </span>
        )}
        <div className="completion-sheet-date">
          {formatShortDate(displaySession.sessionEndTime || new Date().toISOString())}
        </div>
      </div>

      <div className="completion-sheet-name">
        <PlayerNameInput
          key={displaySession.contenderName || 'empty'}
          currentName={displaySession.contenderName || ''}
          onNameChange={onNameChange}
          label="Name on the leaderboard"
        />
      </div>

      <button
        className="completion-sheet-primary"
        data-testid="completion-primary"
        onClick={handlePost}
      >
        Post to the leaderboard
      </button>
      <button className="completion-sheet-secondary" onClick={onClose}>
        Not now
      </button>
    </Sheet>
  );
};

export default CompletionSheet;
